import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiDownload, ApiError } from '../api/client';
import { formatDate } from '../lib/format';
import { btn } from './FilterBar';
import { useToast } from './Toast';

/**
 * Member details. Ports frmKlientatEdit{Stargym,Missfit}.
 *
 * Read-only by design: despite the "Edit" name, those desktop forms contain no
 * UPDATE at all. Their two buttons downloaded the profile photo and generated a
 * details PDF, both of which are here.
 */

interface MemberDetail {
  id: number;
  first_name: string;
  last_name: string;
  birthday: string;
  gender: string;
  phone_number: string;
  email: string;
  package_type: string;
  created_at: string | null;
  end_at: string | null;
  terms_accepted: number;
  hasPhoto: number;
  active: boolean;
}

export default function MemberModal({
  biznesi,
  memberId,
  onClose,
}: {
  biznesi: string;
  memberId: number;
  onClose: () => void;
}) {
  const toast = useToast();

  const memberQuery = useQuery({
    queryKey: ['klient', biznesi, memberId],
    queryFn: () => apiGet<MemberDetail>(`/klientat/${biznesi}/klienti/${memberId}`),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function download(path: string) {
    try {
      await apiDownload(path);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Shkarkimi dështoi.');
    }
  }

  const m = memberQuery.data;
  const fields: [string, string][] = m
    ? [
        ['Gjinia', m.gender || '—'],
        ['Ditëlindja', formatDate(m.birthday)],
        ['Email', m.email || '—'],
        ['Telefoni', m.phone_number || '—'],
        ['Paketa', `${m.package_type} muaj`],
        ['Regjistruar', formatDate(m.created_at)],
        ['Vlen deri', formatDate(m.end_at)],
        ['Kushtet e pranuara', Number(m.terms_accepted) === 1 ? 'Po' : 'Jo'],
      ]
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">
              {m ? `${m.first_name} ${m.last_name}` : 'Klienti'}
            </h2>
            {m && (
              <span
                className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                  m.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'
                }`}
              >
                {m.active ? 'Antarësim aktiv' : 'Antarësimi ka skaduar'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Mbyll
          </button>
        </div>

        {memberQuery.isLoading && <div className="py-10 text-center text-muted">Duke ngarkuar…</div>}

        {m && (
          <>
            <div className="flex flex-wrap gap-4">
              {Number(m.hasPhoto) === 1 ? (
                <img
                  // Served from the API so the session cookie applies; the blob
                  // has no recorded MIME type, the server sniffs it.
                  src={`/api/klientat/${biznesi}/klienti/${m.id}/foto`}
                  alt={`Fotoja e ${m.first_name} ${m.last_name}`}
                  className="h-36 w-36 rounded-xl border border-slate-200 object-cover"
                />
              ) : (
                <div className="flex h-36 w-36 items-center justify-center rounded-xl border border-dashed border-slate-300 text-xs text-muted">
                  Pa foto
                </div>
              )}

              <dl className="min-w-56 flex-1 space-y-1.5 text-sm">
                {fields.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <dt className="text-muted">{label}</dt>
                    <dd className="text-right font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {Number(m.hasPhoto) === 1 && (
                <button
                  type="button"
                  onClick={() => download(`/klientat/${biznesi}/klienti/${m.id}/foto`)}
                  className={btn.secondary}
                >
                  Shkarko foton
                </button>
              )}
              <button
                type="button"
                onClick={() => download(`/klientat/${biznesi}/klienti/${m.id}/dokument`)}
                className={btn.secondary}
              >
                Shkarko detajet
              </button>
              {/*
                The desktop's btnSaveDocument produced two files per member: the
                short details sheet above and the full contract with the Rules
                and Regulations. Both are here.
              */}
              <button
                type="button"
                onClick={() => download(`/klientat/${biznesi}/klienti/${m.id}/kontrata`)}
                className={btn.primary}
              >
                Shkarko kontratën
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
